<script setup lang="ts">
/**
 * DetailPage —— `/detail/:sectionId/:slug?` 路由组件（F10，ADR-0001）。
 *
 * 职责：按路由 detail key 查 Section，渲染详情状态分支、短 URL 修正和
 * page-banner；四分区主体拆到 `<DetailPageBody>`。
 *
 * **数据来源**：Store Aggregate 含完整详情数据；深链 idle 时 bootstrap。
 *
 * **Fallback**：detail key 未命中或非预览访问 Draft → 显式「内容不存在」
 * 并 `console.error` 保留 detail key 与原因。
 *
 * **URL 修正**：旧 Guid 深链命中 Section 后，`replaceState` 为短 URL，
 * 保留 query/hash，不触发 Aggregate 重拉。
 *
 * **Preview 透传**：预览态可渲染 Draft，并将 token 透传给子组件。
 */
import { computed, nextTick, onMounted, watch } from 'vue';
import { useRoute, useRouter, RouterLink } from 'vue-router';

import DetailPageBody from '@/pages/DetailPageBody.vue';
import CriticalFailureBanner from '@/components/CriticalFailureBanner.vue';
import { useShowcase } from '@/store/use-showcase';
import {
  detailPathFor,
  findSectionByDetailKey,
} from '@/composables/detail-path';
import { copy } from '@/i18n/zh-CN';
import type { AggregateView, PublicSectionView } from '@/api/types';

const props = defineProps<{
  readonly sectionId: string;
  readonly slug?: string;
}>();

const route = useRoute();
const router = useRouter();
const store = useShowcase();

onMounted(() => {
  if (store.state.status === 'idle') {
    void store.bootstrap();
  }
});

/** Store.aggregate 按短 slug / 旧 sectionId 查 Section；aggregate 为 null 时返 undefined。 */
function findSectionByRouteKey(
  aggregate: AggregateView | null,
  routeKey: string,
): PublicSectionView | undefined {
  if (aggregate === null) {
    return undefined;
  }
  return findSectionByDetailKey(aggregate.sections, routeKey);
}

const section = computed(() => findSectionByRouteKey(store.state.aggregate, props.sectionId));
const isPreview = computed(() => store.state.isPreview);
const previewToken = computed(() => store.state.previewToken);
const isSkeleton = computed(
  () => store.state.status === 'idle' || store.state.status === 'loading',
);
const isCritical = computed(() => store.state.status === 'failed');

interface DetailFallback {
  readonly reason: 'not_found' | 'unpublished';
}

const fallback = computed<DetailFallback | null>(() => {
  if (isSkeleton.value || isCritical.value) {
    return null;
  }
  const sec = section.value;
  if (sec === undefined) {
    return { reason: 'not_found' };
  }
  if (sec.publishStatus !== 'Published' && !isPreview.value) {
    return { reason: 'unpublished' };
  }
  return null;
});

watch(
  () => fallback.value,
  (value) => {
    if (value === null) {
      return;
    }
    // eslint-disable-next-line no-console
    console.error(
      `[DetailPage] detail key "${props.sectionId}" unavailable: ${value.reason}`,
    );
  },
  { immediate: true },
);

/**
 * URL 修正：section 命中且当前路径不是短 URL → replaceState 修正，保留
 * 当前 query（?preview=）与 hash，不触发 Aggregate 重拉。
 *
 * 用 `router.resolve` 生成完整 URL 而非手搓 URLSearchParams——`LocationQuery`
 * 值可为 `string | string[] | null`，`router.resolve` 正确处理数组态
 * query（如 `?preview=a&preview=b`），避免 `as Record<string,string>` 丢数组。
 */
watch(
  () => [section.value, props.slug] as const,
  ([sec]) => {
    if (sec === undefined || fallback.value !== null) {
      return;
    }
    const resolved = router.resolve({
      path: detailPathFor(sec),
      query: route.query,
      hash: route.hash,
    });
    if (route.fullPath === resolved.fullPath) {
      return;
    }
    globalThis.history.replaceState(null, '', resolved.fullPath);
  },
  { immediate: true },
);

/**
 * `<guid>#<anchor>` 段落锚点滚动（F13，ADR-0014）。section 命中后，空 hash
 * 滚到详情页顶部；非空 hash 在 DOM 渲染后滚动到 bodyHtml 内同名元素。找不到
 * 元素 → 滚到详情页顶部 + `console.warn`（Debug-First：不静默无响应）。
 * merged 同 Guid 入口用 `#materials` 聚焦 MaterialList 区。
 */
watch(
  () => [section.value, route.hash] as const,
  ([sec, hash], previous) => {
    if (sec === undefined || fallback.value !== null) {
      return;
    }
    if (hash === '') {
      const previousHash = previous?.[1] ?? '';
      if (previousHash !== '') {
        void nextTick().then(scrollDetailTop);
      }
      return;
    }
    const id = hash.startsWith('#') ? hash.slice(1) : hash;
    if (id === '') {
      return;
    }
    void nextTick().then(() => scrollDetailAnchor(id));
  },
  { immediate: true },
);

function scrollDetailTop(): void {
  window.scrollTo(0, 0);
}

function scrollDetailAnchor(id: string): void {
  const element = document.getElementById(id);
  if (element === null) {
    // eslint-disable-next-line no-console
    console.warn(`[DetailPage] anchor element not found: "${id}"`);
    scrollDetailTop();
    return;
  }
  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
</script>

<template>
  <main :class="$style.page" data-testid="detail-page">
    <section :class="$style.banner" data-shell="page-banner" data-testid="detail-banner">
      <RouterLink :class="$style.backLink" to="/" data-testid="detail-back-home">
        {{ copy.detail.backHome }}
      </RouterLink>
      <h1 v-if="section !== undefined" :class="$style.bannerTitle">{{ section.title }}</h1>
    </section>

    <div v-if="isSkeleton" :class="$style.skeleton" data-testid="detail-skeleton">
      {{ copy.app.loading }}
    </div>

    <CriticalFailureBanner v-else-if="isCritical" :error="store.state.error" />

    <div
      v-else-if="fallback !== null"
      :class="$style.fallback"
      data-testid="detail-fallback"
      role="alert"
    >
      <h1 :class="$style.fallbackTitle">{{ copy.fallback.detail.title }}</h1>
      <p :class="$style.fallbackDesc">{{ copy.fallback.detail.description }}</p>
      <RouterLink :class="$style.fallbackLink" to="/">{{ copy.detail.backHome }}</RouterLink>
    </div>

    <DetailPageBody
      v-else-if="section !== undefined"
      :section="section"
      :sections="store.state.aggregate!.sections"
      :section-id="props.sectionId"
      :preview-token="previewToken"
      :pdf-cache-version="store.state.contentVersion"
    />
  </main>
</template>

<style module>
.page {
  min-height: 60vh;
  padding-bottom: var(--space-6);
}

/**
 * page-banner（F17 对齐模板 .page-banner）：banner1.png 背景图 + ::before
 * 红色遮罩（琼台红 rgba 0.7）+ 居中标题。返回首页链接绝对定位左上角，
 * z-index 高于遮罩。遮罩用 ::before 伪元素，背景图走 token。
 */
.banner {
  position: relative;
  height: var(--detail-banner-height);
  max-width: var(--container-max);
  margin: 0 auto var(--space-5);
  padding: 0 var(--space-3);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-brand-primary);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.backLink {
  position: absolute;
  top: var(--space-2);
  left: var(--space-3);
  z-index: 2;
  color: var(--color-bg-primary);
  font-size: var(--font-size-sm);
  font-weight: 600;
}

.backLink:hover {
  color: var(--color-brand-accent);
}

.bannerTitle {
  position: relative;
  z-index: 2;
  color: var(--color-bg-primary);
  font-size: var(--template-page-banner-title-size);
  font-weight: 700;
  letter-spacing: var(--template-page-banner-title-letter-spacing);
  text-shadow: var(--template-page-banner-title-shadow);
  border-bottom: var(--template-page-banner-title-border-width) solid var(--color-brand-accent);
  padding-bottom: var(--space-1);
}

.skeleton {
  max-width: var(--container-max);
  margin: 0 auto;
  padding: var(--space-5) var(--space-3);
  color: var(--color-text-muted);
  text-align: center;
}

.fallback {
  max-width: var(--container-max);
  margin: 0 auto;
  padding: var(--space-6) var(--space-3);
  text-align: center;
}

.fallbackTitle {
  color: var(--color-brand-primary);
  font-size: var(--font-size-xl);
  margin-bottom: var(--space-2);
}

.fallbackDesc {
  color: var(--color-text-secondary);
  margin-bottom: var(--space-3);
}

.fallbackLink {
  display: inline-block;
  padding: var(--space-1) var(--space-3);
  border: 1px solid var(--color-brand-primary);
  border-radius: var(--radius-md);
  color: var(--color-brand-primary);
}
</style>
